import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth'; // Import Firebase reset function
import MfaCodeEntry from './MfaCodeEntry';
import '../styles/Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [resetMessage, setResetMessage] = useState(''); // Message for reset link
  const navigate = useNavigate();
  const {
    login,
    mfaRequired,
    mfaPendingData,
    handleMfaSuccess,
    handleMfaCancel,
    accessDenied,
    clearAccessDenied,
  } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await login(email, password);
      // Only navigate if MFA is not required (use return value, not stale state)
      if (!result?.mfaRequired && !result?.accessDenied) {
        navigate('/');
      }
      // If MFA is required, the MfaCodeEntry component will be shown
    } catch (error) {
      setError('Failed to login. Please check your credentials.');
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    try {
      const auth = getAuth(); // Get Firebase auth instance
      await sendPasswordResetEmail(auth, email);
      setResetMessage('Password reset email sent! Please check your inbox.');
      setError(null);
    } catch (error) {
      setError('Failed to send password reset email. Please try again.');
    }
  };

  const onMfaSuccess = async () => {
    try {
      await handleMfaSuccess();
      navigate('/');
    } catch (error) {
      setError('MFA verification failed. Please try again.');
    }
  };

  const onMfaCancel = async () => {
    await handleMfaCancel();
    setError('Login cancelled.');
  };

  const onAccessDeniedBack = async () => {
    setError(null);
    setResetMessage('');
    await clearAccessDenied();
  };

  const isDeviceRestricted = accessDenied?.reason === 'device_restricted';

  // Show MFA code entry if required
  if (mfaRequired && mfaPendingData) {
    return (
      <MfaCodeEntry
        userId={mfaPendingData.userResponse.portal_id}
        reason={mfaPendingData.reason}
        triggerReason={mfaPendingData.triggerReason}
        deviceFingerprint={mfaPendingData.deviceFingerprint}
        onSuccess={onMfaSuccess}
        onCancel={onMfaCancel}
      />
    );
  }

  if (accessDenied) {
    return (
      <div className="login-container">
        <div className="access-denied-panel">
          <h2>Access Denied</h2>
          {isDeviceRestricted ? (
            <>
              <p><strong>Mobile phone access is blocked.</strong></p>
              <p>Accessing or updating patient records from a phone is <strong>against hospital policy</strong> and is <strong>not permitted</strong>.</p>
              <p>This system was not designed for phone use, and doing so <strong>increases the risk of clinical and documentation errors</strong>.</p>
            </>
          ) : (
            <p>{accessDenied.message || 'Your account is not allowed to access the portal from this device or network.'}</p>
          )}
          {accessDenied.reason && <p className="access-denied-reason">Reason: {accessDenied.reason}</p>}
          <button type="button" onClick={onAccessDeniedBack}>Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Email:</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Password:</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        {error && <p className="error-message">{error}</p>}
        {resetMessage && <p className="success-message">{resetMessage}</p>} {/* Display reset success message */}
        <button type="submit">Login</button>
        <p className="forgot-password">
          Forgot your password?{' '}
          <span onClick={handlePasswordReset} className="reset-link">
            Reset Password
          </span>
        </p>
      </form>
    </div>
  );
};

export default Login;
